import { useEffect, useRef, useState } from 'react';
import { DialogActions, DialogContent, SwipeableDrawer, Tab, Tabs, Tooltip } from '@mui/material';
import { PickerFormItem, PickerFormType } from './PickerFormTypes';
import styles from './PickerForm.module.scss';
import useMobileExpanded from '../../../../lib/hooks/useMobileExpanded';
import Button from '../../../common/Button/Button';
import WithDevice from '../../../common/WithDevice/WithDevice';
import { LeftArrow } from '../../../../lib/icons/LeftArrow';
import { PickerSourceList } from './PickerSourceList';
import { classes } from '../../../../lib/utils/cssUtils';
import SelectedItemsList from './SelectedItemsList';
import { notificationsIds, pickerFormIds } from '../../../../lib/tests/testIds';
import { CY_MODAL_ASSIGN_BUTTON } from '../../../../lib/constants/cypress';
import { COMMON_LABELS, FORM_PICKER_LABELS } from '../../../../lib/constants/labelKeys';
import { Label } from '../../../common/Label/Label';
import Notification, { NotificationType } from '../Notification/Notification';
import RefreshLoader from '../../../common/RefreshLoader/RefreshLoader';
import BaseDialog from '../../../common/BaseDialog/BaseDialog';
import BaseDialogHeading from '../../../common/BaseDialog/BaseDialogHeading';
import ErrorBoundary from '../../../common/ErrorBoundary/ErrorBoundary';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';
import TeamSizeWarning from './TeamSizeWarning';
import { distinctValuesByProp } from '../../../../lib/utils/distinctValues';

export function PickerForm({
  value,
  onSubmit,
  preselectedItems,
  labels,
  isOpen,
  onCancel,
  customClasses,
  loading,
  error,
  teamSizeWarningMessage,
  totalMembers,
  allowEmpty,
}: PickerFormType) {
  const summaryId = 'selected-summary';
  const selectClicks = useRef(0);
  const [selectedItems, setSelectedItems] = useState<PickerFormItem[]>(preselectedItems ?? []);
  const [activeTab, setActiveTab] = useState(value[0]?.titleKey);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTooltipOpened, setIsToolTipOpened] = useState(false);
  const [isDrawerOpened, setDrawerOpened, toggleDrawerOpened] = useMobileExpanded(false);
  const { getParsedErrorMessage } = useErrorMapping();
  const drawerContainerRef = useRef<HTMLDivElement>(null);
  const [isTeamSizeWarningVisible, setIsTeamSizeWarningVisible] = useState(!!teamSizeWarningMessage);

  const isSelected = (item: PickerFormItem) => selectedItems.some(selectedItem => selectedItem.id === item.id);

  const selectItem = (item: PickerFormItem) => {
    setSelectedItems(items => (isSelected(item) ? items : [...items, item]));
    selectClicks.current += 1;
    setIsTeamSizeWarningVisible(true);
  };

  const deselectItem = (item: PickerFormItem) => {
    setSelectedItems(prev => prev.filter(selectedItem => selectedItem.id !== item.id));
    selectClicks.current += 1;
    setIsTeamSizeWarningVisible(true);
  };

  const submit = async () => {
    setIsLoading(() => true);

    onSubmit(selectedItems)
      ?.then(() => setDrawerOpened(false))
      .catch((submitError: any) => {
        setErrorMessage(() => getParsedErrorMessage(submitError));
        setIsError(true);
        setDrawerOpened(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const selectedSummary = () => (
    <span className={styles.assignedSummary} id={summaryId} role="status">
      {selectedItems.length} <Label name={labels.assignedSummaryKey} />
    </span>
  );

  useEffect(() => {
    setSelectedItems(items =>
      distinctValuesByProp([...items, ...(preselectedItems ?? [])], 'id').filter(item =>
        value.some(v => v.list.some(current => v.idFunction(item) === v.idFunction(current))),
      ),
    );
  }, [value, preselectedItems]);

  useEffect(() => {
    setIsError(false);

    if (selectClicks.current === 1) {
      setIsToolTipOpened(() => true);
    }
  }, [selectClicks.current]);

  useEffect(() => {
    setIsToolTipOpened(false);
  }, [isDrawerOpened]);

  useEffect(() => {
    setDrawerOpened(false);
  }, [isOpen]);

  return (
    <BaseDialog open={isOpen} onClose={onCancel}>
      <BaseDialogHeading closeButtonProps={{ onClick: onCancel }}>{labels.dialogTitle}</BaseDialogHeading>
      <DialogContent classes={{ root: styles.dialogContent }}>
        {(loading || isLoading) && <RefreshLoader />}
        <ErrorBoundary error={error} className={styles.errorInfo} internal>
          <div className={styles.dialogInsideContainer}>
            <Tabs
              indicatorColor="secondary"
              textColor="inherit"
              variant="fullWidth"
              value={activeTab}
              className={styles.tabsRoot}
              onChange={(_, newValue) => setActiveTab(newValue)}
            >
              {value.map(({ titleKey }) => (
                <Tab
                  key={titleKey}
                  value={titleKey}
                  label={<Label name={titleKey} />}
                  classes={{ root: classes(styles.tabButton, { [styles.singleTabButton]: value.length === 1 }) }}
                  id={`${titleKey}-tab`}
                  aria-controls={`${titleKey}-tabpanel`}
                />
              ))}
            </Tabs>
            {value
              .filter(({ titleKey }) => titleKey === activeTab)
              .map(v => (
                <PickerSourceList
                  {...v}
                  key={v.titleKey}
                  onSelect={selectItem}
                  onDeselect={deselectItem}
                  isSelectedFn={isSelected}
                />
              ))}
          </div>
          <WithDevice desktop>
            <div className={styles.dialogInsideContainer}>
              <div className={classes(styles.assignedContainerTitle, { [styles.assignedContainerError]: isError })}>
                <Label name={labels.selectedItemsHeadingKey} />
              </div>
              <SelectedItemsList
                deselectItem={deselectItem}
                labels={labels}
                selectedItems={selectedItems}
                value={value}
                summaryId={summaryId}
              />
            </div>
          </WithDevice>
        </ErrorBoundary>
      </DialogContent>
      <WithDevice mobile tablet>
        <div ref={drawerContainerRef} data-testid={pickerFormIds.mobileDrawer} />
        <SwipeableDrawer
          swipeAreaWidth={0}
          anchor="bottom"
          open={isDrawerOpened}
          onOpen={() => setDrawerOpened(true)}
          onClose={() => setDrawerOpened(false)}
          classes={{
            root: styles.drawerRoot,
            modal: styles.mobileModal,
            paper: classes(styles.drawerPaper, styles.initialDrawerPaper),
          }}
          ModalProps={{
            container: () => drawerContainerRef.current,
          }}
          variant="temporary"
          SlideProps={{
            onEnter: node => {
              node.classList.remove(styles.initialDrawerPaper);
            },
            onExiting: node => {
              node.classList.add(styles.initialDrawerPaper);
            },
          }}
        >
          <div className={styles.drawerSwipeArea}>
            <Tooltip
              title={<Label name={FORM_PICKER_LABELS.TOOLTIP_TEXT} />}
              arrow
              placement="top"
              open={isTooltipOpened}
              classes={{ tooltip: styles.tooltip, arrow: styles.tooltipArrow }}
            >
              <button
                onClick={toggleDrawerOpened}
                className={styles.drawerSwipeButton}
                aria-hidden={false}
                data-testid={pickerFormIds.openDrawerButton}
              >
                <LeftArrow
                  className={classes(styles.drawerSwipeIcon, { [styles.drawerSwipeIconOpened]: isDrawerOpened })}
                />
              </button>
            </Tooltip>
          </div>
          <div className={styles.drawerVisibleContent}>{selectedSummary()}</div>
          <SelectedItemsList
            deselectItem={deselectItem}
            labels={labels}
            selectedItems={selectedItems}
            value={value}
            summaryId={summaryId}
          />
        </SwipeableDrawer>
      </WithDevice>
      <DialogActions classes={{ root: styles.dialogActions }}>
        <Notification
          content={errorMessage}
          type={NotificationType.Error}
          showCloseButton={false}
          transitionAnimation
          open={isError}
          ariaRole="alert"
          wrapperClass={styles.errorsContainer}
          data-testid={notificationsIds.pickerFormNotification}
        />
        {teamSizeWarningMessage && isTeamSizeWarningVisible && !isDrawerOpened && (
          <TeamSizeWarning
            message={teamSizeWarningMessage}
            selectedItems={selectedItems}
            onClickHandler={() => setIsTeamSizeWarningVisible(false)}
            totalMembers={totalMembers}
          />
        )}
        <div
          className={classes(styles.dialogActionsButtons, customClasses?.dialogActionButtons)}
          data-cy={CY_MODAL_ASSIGN_BUTTON}
        >
          <WithDevice desktop>{selectedSummary()}</WithDevice>
          <Button onClick={onCancel} variant="outlined">
            <Label name={COMMON_LABELS.DIALOG.CANCEL} />
          </Button>
          <Button onClick={submit} disabled={(!allowEmpty && !selectedItems.length) || isLoading || isError}>
            <Label name={labels.submitButtonLabelKey} />
          </Button>
        </div>
      </DialogActions>
    </BaseDialog>
  );
}
